import type { SettlementDecision, SettlementDto, SettlementsDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText } from '../../ui';

const KIND: Record<SettlementDto['tipo'], string> = { pagamento: 'Pagamento', incasso: 'Incasso', giroconto: 'Giroconto' };
const euro = (v: string | null) => (v === null ? '' : `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

/**
 * Pagamenti, incassi e giroconti di una voce (ADR 0245): nel movimento ciò che salda, nella fattura o nella rata i
 * movimenti che la saldano. I collegamenti certi li ha fatti il sistema; le proposte si confermano o si scartano, e ogni
 * collegamento si può togliere (non torna).
 */
export function Settlements({ tipo, rif }: { tipo: string; rif: string }) {
  const { isManager } = useTenant();
  const q = useTenantQuery<SettlementsDto>(['model', 'settlements', tipo, rif], `/model/settlements?${new URLSearchParams({ tipo, rif }).toString()}`, { enabled: isManager });
  const list = q.data?.collegamenti ?? [];
  if (!list.length) return null;
  const proposals = list.filter((s) => s.stato === 'proposto').length;
  return (
    <Card>
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Pagamenti e incassi <Badge tone="blue">{list.length - proposals}</Badge>
        {proposals > 0 && <Badge tone="amber">{proposals} da confermare</Badge>}
      </h2>
      <ul className="mt-3 divide-y divide-slate-100">
        {list.map((s) => (
          <SettlementRow key={s.id} s={s} self={{ tipo, rif }} />
        ))}
      </ul>
    </Card>
  );
}

function SettlementRow({ s, self }: { s: SettlementDto; self: { tipo: string; rif: string } }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Dalla parte del movimento si vede ciò che salda; dalla parte della cosa, il movimento.
  const other = s.movimento.tipo === self.tipo && s.movimento.rif === self.rif ? s.cosa : s.movimento;
  const decide = async (body: SettlementDecision) => {
    setBusy(true);
    setError(null);
    try {
      await api(t(`/model/settlements/${s.id}`), { body });
      await queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model', 'settlements'] });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="text-slate-500">{KIND[s.tipo]}</span>
        <a className="font-medium text-accent hover:underline" href={`#/t/${tenant.id}/e/${other.tipo}/${encodeURIComponent(other.rif)}`}>
          {other.nome ?? other.rif}
        </a>
        {other.data && <span className="text-xs text-slate-500">del {new Date(other.data).toLocaleDateString('it-IT')}</span>}
        <span className="font-medium">{euro(s.importo)}</span>
        {s.stato === 'proposto' ? <Badge tone="amber">Da confermare</Badge> : s.origine === 'persona' ? <Badge tone="blue">Collegato da te</Badge> : s.stato === 'confermato' ? <Badge tone="blue">Confermato</Badge> : <Badge tone="green">Certo</Badge>}
      </div>
      {s.perche && <p className="mt-1 text-xs text-slate-500">{s.perche.charAt(0).toUpperCase() + s.perche.slice(1)}</p>}
      {isManager && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {s.stato === 'proposto' && (
            <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'confermato' })}>
              Conferma
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={() => void decide({ decisione: 'scartato' })}>
            {s.stato === 'proposto' ? 'Scarta' : 'Togli'}
          </Button>
        </div>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </li>
  );
}
